import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Plus, Loader2, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ProductAutocomplete
 * --------------------------------------------------------------
 * Reusable autocomplete input for any procurement / finance form.
 * Calls GET /api/financial/products?search=XX to fetch matches.
 * Also lets the user create a new product on the fly (POST to the
 * same endpoint) so RFQ/LOI/PO authors never have to leave the
 * form they're writing.
 *
 * Props:
 *   value         — current product name string
 *   onSelect(p)   — called with the full product object when a
 *                   suggestion is picked. For ad-hoc typed input,
 *                   called with { name: typedValue } on blur.
 *   onChange(str) — called on every keystroke (for controlled form)
 *   placeholder
 *   testId        — data-testid prefix for e2e tests
 *   disabled
 *   inputClassName
 */
const ProductAutocomplete = ({
  value,
  onSelect,
  onChange,
  placeholder = 'Type to search products…',
  testId = 'product-autocomplete',
  disabled = false,
  inputClassName = 'w-full px-3 py-2 border border-gray-300 rounded-md',
}) => {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;

  // Keep local query in sync when parent resets value
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Click-outside closes the dropdown
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const fetchSuggestions = useCallback(async (term) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '15' });
      if (term) params.set('search', term);
      const r = await axios.get(`${API_URL}/api/financial/products?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setResults(Array.isArray(r.data) ? r.data : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleFocus = () => {
    setOpen(true);
    if (results.length === 0) fetchSuggestions(query);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange?.(v);
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 220);
  };

  const pick = (p) => {
    setQuery(p.name);
    onChange?.(p.name);
    onSelect?.(p);
    setOpen(false);
  };

  const createAndPick = async () => {
    const name = (query || '').trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/financial/products`,
        { name, price: 0, currency: 'AED', tax_rate: 5, category: 'goods', active: true },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      pick(r.data);
    } catch {
      // swallow — user can retry or fill manually
    } finally {
      setCreating(false);
    }
  };

  const exactMatch = results.some(
    (p) => (p.name || '').toLowerCase().trim() === (query || '').toLowerCase().trim()
  );
  const canCreate = !!query.trim() && !exactMatch && !loading;

  return (
    <div ref={boxRef} className="relative" data-testid={testId}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          className={`${inputClassName} pl-9`}
          placeholder={placeholder}
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          disabled={disabled}
          data-testid={`${testId}-input`}
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
        )}
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {results.length === 0 && !loading && (
            <div className="px-3 py-6 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
              <Package className="w-6 h-6 text-gray-300" />
              No products match "{query}".
            </div>
          )}

          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => pick(p)}
              className="w-full text-left px-3 py-2.5 hover:bg-[#00B4D8]/10 transition-colors border-b border-gray-100 last:border-b-0 flex items-start justify-between gap-3"
              data-testid={`${testId}-suggestion-${p.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#2B2D42] text-sm truncate">{p.name}</div>
                <div className="text-[11px] text-gray-500 flex gap-3 mt-0.5">
                  {p.sku && <span>SKU: <span className="font-mono">{p.sku}</span></span>}
                  {p.category && <span className="uppercase">{p.category}</span>}
                  {p.tax_rate != null && <span>Tax {p.tax_rate}%</span>}
                </div>
                {p.description && (
                  <div className="text-[11px] text-gray-400 truncate mt-0.5">{p.description}</div>
                )}
              </div>
              <div className="text-xs font-mono text-[#023E8A] font-semibold whitespace-nowrap">
                {p.currency} {Number(p.price || 0).toFixed(2)}
              </div>
            </button>
          ))}

          {canCreate && (
            <button
              type="button"
              onClick={createAndPick}
              disabled={creating}
              className="w-full text-left px-3 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-semibold border-t border-emerald-200 flex items-center gap-2"
              data-testid={`${testId}-create-new`}
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create new product "{query}"
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductAutocomplete;
